/**
 * @file VerificationCodeList.tsx
 * @description Список кодов подтверждения одного поисковика (phase19.16,
 * партия Р3в, v0.19.64; SEO1): до 5 строк, в каждую можно вставить код или
 * мета-тег целиком — под строкой «Код: …», который достал shared
 * `parseVerificationCode`; неразборчивое — ошибкой под строкой. Строка
 * удаляется кнопкой ✕ (48 px); «+ Добавить код», пока кодов меньше 5.
 *
 * Строки ввода живут здесь (тег целиком показываем как вставили), наружу
 * уходят только разобранные коды и признак «есть неразборчивые».
 */

import { parseVerificationCode, SEO_VERIFICATION_MAX, type SeoVerificationEngine } from "@chaiberi/shared";
import { Plus, X } from "lucide-react";
import { useId, useState } from "react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { parseVerificationRows } from "../../../lib/site-seo/verification-rows";

interface Props {
  engine: SeoVerificationEngine;
  label: string;
  hint: string;
  /** Коды с сервера — начальные строки. */
  initial: readonly string[];
  disabled: boolean;
  onChange: (codes: string[], invalid: boolean) => void;
}

export function VerificationCodeList({ engine, label, hint, initial, disabled, onChange }: Props) {
  const baseId = useId();
  const [rows, setRows] = useState<string[]>(() => (initial.length > 0 ? [...initial] : [""]));

  function update(next: string[]) {
    setRows(next);
    const parsed = parseVerificationRows(next, engine);
    onChange(parsed.codes, parsed.invalid);
  }

  return (
    <fieldset className="flex min-w-0 flex-col gap-2" disabled={disabled}>
      <legend className="text-sm font-medium text-on-surface">{label}</legend>
      <p className="type-caption text-on-surface-variant">{hint}</p>
      {rows.map((row, index) => {
        const id = `${baseId}-${index}`;
        const code = row.trim() ? parseVerificationCode(row, engine) : null;
        const pasted = code !== null && code !== row.trim();
        return (
          <div key={index} className="flex min-w-0 flex-col gap-1">
            <Label htmlFor={id} className="sr-only">{`${label}: код ${index + 1}`}</Label>
            <div className="flex min-w-0 items-center gap-1">
              <Input
                id={id}
                className="min-w-0 flex-1 font-mono"
                value={row}
                placeholder={`<meta name="…" content="…" />`}
                aria-invalid={row.trim() !== "" && code === null}
                onChange={(e) => update(rows.map((r, i) => (i === index ? e.target.value : r)))}
              />
              <Button variant="ghost" size="icon" className="size-12 shrink-0" aria-label={`Удалить код ${index + 1}`} onClick={() => update(rows.length > 1 ? rows.filter((_, i) => i !== index) : [""])}>
                <X aria-hidden />
              </Button>
            </div>
            {row.trim() && code === null ? <p className="type-caption text-destructive">Не похоже на код подтверждения — скопируйте мета-тег целиком.</p> : null}
            {pasted ? <p className="type-caption text-on-surface-variant">Код: <span className="font-mono">{code}</span></p> : null}
          </div>
        );
      })}
      {rows.length < SEO_VERIFICATION_MAX ? (
        <Button variant="outline" className="self-start max-sm:w-full" onClick={() => update([...rows, ""])}>
          <Plus aria-hidden />
          Добавить код
        </Button>
      ) : (
        <p className="type-caption text-on-surface-variant">Не больше {SEO_VERIFICATION_MAX} кодов на поисковик.</p>
      )}
    </fieldset>
  );
}
